/**
 * Applies a `model3dLight` action to the lights of a loaded model.
 *
 * The Designer preview and the exported runtime consume the same action shape, so the
 * preview stays a renderer for the real action rather than a second light-control path.
 *
 * Intensity is interpreted as a 0..100 percentage of the intensity the light was loaded
 * with. glTF intensities are absolute photometric values (a Blender point light easily
 * exports as ~54000 candela), so a raw slider value would be meaningless on its own.
 * Scaling always starts from `baseIntensity`, never from the current intensity, so
 * repeated slider updates cannot compound.
 */
import { getH3dLightProperty } from '../actions/model3dLightWireFormat.js'

export const H3D_LIGHT_INTENSITY_SCALE = 100

/**
 * @param {Map<string, { light: import('three').Light, baseIntensity: number }>} lightsById
 * @param {{ light: string, property: string, value: number|string }} action
 * @returns {boolean} whether the action addressed a known light and property
 */
export function applyH3dLightAction(lightsById, action) {
  const property = getH3dLightProperty(action?.property)
  if (!property) return false

  const entry = lightsById?.get?.(String(action?.light ?? ''))
  if (!entry?.light) return false

  if (property.id === 'intensity') {
    const percentage = Number(action.value)
    if (!Number.isFinite(percentage)) return false
    const clamped = Math.min(Math.max(percentage, 0), H3D_LIGHT_INTENSITY_SCALE)
    entry.light.intensity = (entry.baseIntensity * clamped) / H3D_LIGHT_INTENSITY_SCALE
    return true
  }
  return false
}
